import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { _ } from '/@/locale';
import { getRecommendationBlockImage } from '/@/store/private-data/recommendation-pdfs/actions';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Recommendation } from '/@/types/recommendation';
import type { BlockImageKey } from '/@/views/recommendations/v2/block-image';

type Feedback = { severity: 'success' | 'error' | 'info'; message: string };

const canWriteImagesToClipboard = () =>
    typeof window !== 'undefined' &&
    typeof window.ClipboardItem !== 'undefined' &&
    typeof navigator.clipboard?.write === 'function';

/**
 * Fetches one v2 result-page block as a PNG and writes it to the OS clipboard.
 *
 * The image comes from a synchronous backend call (roughly 1-3s, a real Puppeteer render), so
 * `copyingBlock` is exposed for the triggering button to show a busy state. When the browser
 * can't write an image to the clipboard -- older Safari, an insecure context, a denied
 * permission -- this falls back to opening the PNG in a new tab rather than failing silently,
 * per the ticket's acceptance criteria.
 */
export const useCopyBlockImage = (recommendation?: Recommendation) => {
    const dispatch = useDispatch();
    const [copyingBlock, setCopyingBlock] = useState<BlockImageKey | undefined>(undefined);
    const [feedback, setFeedback] = useState<Feedback | undefined>(undefined);

    const copyBlock = async (block: BlockImageKey) => {
        if (!recommendation || copyingBlock) return;

        setCopyingBlock(block);
        setFeedback(undefined);
        try {
            const blob: Blob = await dispatch(getRecommendationBlockImage({ recommendation, block }));

            if (canWriteImagesToClipboard()) {
                await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
                setFeedback({ severity: 'success', message: _('¡Imagen copiada! Ya podés pegarla donde quieras.') });
            } else {
                window.open(URL.createObjectURL(blob), '_blank');
                setFeedback({
                    severity: 'info',
                    message: _(
                        'Tu navegador no permite copiar la imagen automáticamente. La abrimos en una pestaña nueva para que la guardes o copies manualmente.'
                    ),
                });
            }
            mpTrackEvent('Seed recommendation block copied as image', { block });
        } catch (error) {
            console.error(error);
            setFeedback({ severity: 'error', message: _('No pudimos copiar la imagen. Intentá de nuevo.') });
        } finally {
            setCopyingBlock(undefined);
        }
    };

    return { copyBlock, copyingBlock, feedback, clearFeedback: () => setFeedback(undefined) };
};
